import React, { useRef, useState, useEffect } from 'react';
import { Dialog, DialogTrigger, DialogContent, DialogClose } from '@/components/atoms/Dialog';
import { useNavigate } from 'react-router-dom';

import ExcelIcon from '@/assets/icons/V3/statistics/ExcelIcon.svg?react';
import { ChevronRight } from 'lucide-react';
import InputCloseGrayIcon from '@/assets/icons/V3/InputCloseGrayIcon.svg?react';
import { Button as PcButton } from '@/components/atoms/PcButton';
import { Button as PreviewButton } from '@/components/atoms/NewPcButton';

import { useAppDispatch, useAppSelector } from '@/redux/store';
import { setUser } from '@/redux/userSlice';
import { useToast } from '@/hooks/V3/useToast';
import useWebview from '@/hooks/V3/useWebview';
import { useLog } from '@/hooks/V3/useLog';
import { useRecoilValue } from 'recoil';
import { deviceUuidState } from '@/store/atom';
import { CPLAT_API_ROUTES, usePost } from '@/api';
import { CplatApiResponse } from '@/types/api';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';
import { ROUTES } from '@/routes';
const ExcelAgreementCTA = () => {
  const [open, setOpen] = useState(false);
  const isPreviewMode = useMembershipPreviewMode();
  const navigate = useNavigate();

  // 비즈니스 로직
  const inputRef = useRef<HTMLInputElement>(null);
  const user = useAppSelector((state) => state.user);

  const [email, setEmail] = useState<string>('');
  const [isEmailValidation, setIsEmailValidation] = useState<boolean>(true);
  const { showToast } = useToast();
  const dispatch = useAppDispatch();
  const { postMessage } = useWebview();
  const { setUserEventHistory } = useLog(user.UserNo);
  const deviceUuid = useRecoilValue(deviceUuidState);

  const { mutate: setEmailAddress } = usePost(CPLAT_API_ROUTES.setEmailAddress);
  const { mutate: getUserInfo } = usePost(CPLAT_API_ROUTES.getUserInfo);
  const { mutate: setUserInfo } = usePost(CPLAT_API_ROUTES.setUserInfo);

  const handleSetUserInfo = () => {
    setUserInfo(
      {
        ...user,
        AdAgreeYn: 'Y',
      },
      {
        onSuccess(res) {
          const { code } = res.data as CplatApiResponse<unknown[]>;
          if (code === '200') {
            handleGetUserInfo();
            postMessage({
              action: 'callApi',
              data: 'getUserInfo',
            });
            setOpen(false);
          } else {
            showToast('warning', '다시 한번 시도해주세요');
          }
        },
      },
    );
  };

  const handleSetEmailAddress = () => {
    setEmailAddress(
      {
        cplatToken: user.CplatToken,
        emailAddress: email,
      },
      {
        onSuccess(res) {
          const { code } = res.data as CplatApiResponse<unknown[]>;
          if (code === '200') {
            showToast('success', '이메일 등록이 완료되었어요!');
            handleSetUserInfo();
            setUserEventHistory({
              eventName: 'register_excel_file_email',
              eventValue: email,
              deviceUuid,
            });
          } else {
            showToast('warning', '다시 한번 시도해주세요');
          }
        },
        onError(error) {
          showToast('warning', error.message);
          console.error(error);
        },
      },
    );
  };

  const handleGetUserInfo = () => {
    getUserInfo(
      {
        cplatToken: user.CplatToken,
      },
      {
        onSuccess(res) {
          const { data, code } = res.data as CplatApiResponse<Array<Parameters<typeof setUser>[0]>>;
          if (code === '200') {
            dispatch(setUser(data[0]));
          }
        },
        onError(error) {
          showToast('warning', error.message);
          console.error(error);
        },
      },
    );
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleanedInput = e.target.value.replace(/\s{2,}/g, ' ');
    setEmail(cleanedInput);
  };

  const handleSubmit = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      inputRef.current?.focus();
      setIsEmailValidation(false);
      return;
    }

    setIsEmailValidation(true);

    handleSetEmailAddress();
  };

  useEffect(() => {
    if (!open || isPreviewMode) return;

    inputRef.current?.focus();
  }, [isPreviewMode, open]);

  // UI
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <div
          className="flex cursor-pointer items-center justify-between rounded-lg bg-gr-50 p-4"
          data-membership-preview-interactive={isPreviewMode ? 'true' : undefined}
        >
          <div className="flex gap-2">
            <div className="flex items-center justify-center gap-2 rounded-full bg-white p-2">
              <ExcelIcon />
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-gr-800 Body1S16">매출 데이터 받아보기</div>
              <div className="text-gr-700 Caption1M12">
                {isPreviewMode
                  ? '구독 시 매월 매출 데이터 파일을 받아볼 수 있어요'
                  : '이메일을 등록하면 엑셀 파일을 발송해 드려요'}
              </div>
            </div>
          </div>
          <div>
            <ChevronRight />
          </div>
        </div>
      </DialogTrigger>
      <DialogContent
        noCloseBtn
        className={
          isPreviewMode ? 'w-[500px] max-w-[calc(100vw-32px)] p-[30px]' : 'max-w-[380px] p-[30px]'
        }
        data-membership-preview-interactive={isPreviewMode ? 'true' : undefined}
      >
        {isPreviewMode ? (
          <MembershipStatisticsPreviewExcelDialog
            onClose={() => setOpen(false)}
            onSubscribe={() => navigate(ROUTES.moreMembership)}
          />
        ) : (
          <div className="flex h-full w-full flex-col gap-[20px]">
            <div className="flex flex-col gap-4">
              <div className="text-gr-900 Title3S18">이메일을 등록하고 매출 파일을 받아보세요!</div>
              <div className="flex flex-col text-gr-700 Body5M14">
                <div>확인해주세요</div>
                <ul className="list-disc pl-5">
                  <li>등록한 이메일로 매 월 10일에 전월의 매출 데이터 파일이 전송돼요</li>
                  <li>이메일 등록 시 마케팅 정보 수신에 동의하는 것으로 간주돼요</li>
                </ul>
              </div>
            </div>
            <div className="relative">
              <input
                ref={inputRef}
                type="email"
                placeholder="sellerbox@naver.com"
                value={email}
                onChange={handleInput}
                className={`w-full rounded-[8px] border px-[10px] py-3 text-gr-800 Body5M14 focus:outline-none focus:ring-2 focus:ring-point-500 ${
                  isEmailValidation ? 'border-gr-300' : 'border-red-400'
                }`}
              />
              {email && (
                <span
                  className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
                  onClick={() => {
                    inputRef.current?.focus();
                    setEmail('');
                  }}
                >
                  <InputCloseGrayIcon />
                </span>
              )}
            </div>
            {!isEmailValidation && (
              <div className="text-xs text-red-500">이메일 형식이 올바르지 않습니다.</div>
            )}
            <div className="flex w-full gap-3">
              <DialogClose asChild>
                <PcButton variant="muted" className="flex-1">
                  닫기
                </PcButton>
              </DialogClose>
              <PcButton
                variant="default"
                className="h-[44px] flex-1"
                onClick={handleSubmit}
                disabled={!email}
              >
                등록하기
              </PcButton>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

type MembershipStatisticsPreviewExcelDialogProps = {
  onClose: () => void;
  onSubscribe: () => void;
};

const MembershipStatisticsPreviewExcelDialog = ({
  onClose,
  onSubscribe,
}: MembershipStatisticsPreviewExcelDialogProps) => (
  <div className="flex flex-col gap-5">
    <div className="flex flex-col gap-1">
      <span className="text-point-500 Caption1M12">멤버십 혜택</span>
      <div className="text-gr-900 Title3S18">매출 데이터 자동 발송</div>
      <div className="text-gr-700 Body5M14">
        구독하면 매월 10일 오전 10시에 전월의 매출 데이터 파일을 받아볼 수 있어요.
      </div>
    </div>
    <div className="rounded-[8px] bg-gr-50 p-4 text-gr-800 Body5M14">
      매출, 정산, 상품별 판매 데이터를 엑셀로 확인할 수 있어요.
    </div>
    <div className="flex gap-3">
      <PreviewButton className="h-[44px] flex-1" variant="secondary" onClick={onClose}>
        계속 둘러보기
      </PreviewButton>
      <PreviewButton className="h-[44px] flex-1" variant="solid" onClick={onSubscribe}>
        구독하고 사용하기
      </PreviewButton>
    </div>
  </div>
);

export default ExcelAgreementCTA;
